<template>
  <transition name="fade">
    <div class="back-to-top-container" v-show="showBackToTop" :style="{ bottom: buttonPosition }">
      <base-button
        :key="buttonKey"
        class="button-top"
        :custom-size="props.buttonSize"
        :min-width="props.buttonSize"
        :custom-border-radius="props.buttonBorderRadius"
        :color="props.buttonColor"
        size="xs"
        @click="scrollToTop"
        transparent
      >
        <base-icon class="mt-1" name="chevron-up" fill="white" width="24px" height="18px"></base-icon>
      </base-button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {ref, onMounted, onBeforeUnmount, computed} from 'vue';
import BaseButton from "@/components/Buttons/BaseButton.vue";
import { useBreakpoint } from '@/composable/ui/useScreen';
import BaseIcon from "@/components/Images/BaseIcon.vue";
import { Color } from '@/store/theme.store';

const props = defineProps({
  buttonSize: {
    type: Number,
    default: 75
  },
  buttonColor: {
    type: String as () => Color,
    default: 'primary'
  },
  buttonBorderRadius: {
    type: String,
    default: '50px'
  },
  textColor: {
    type: String as () => Color,
    default: 'white'
  },
  textSize: {
    type: String as () => 'xs' | 'sm' | 'md' | 'lg',
    default: 'lg'
  }
});

const breakpoints = useBreakpoint();
const showBackToTop = ref(false);
const buttonPosition = ref('3rem');
const buttonKey = ref(0);

function scrollToTop() {
  const duration = 700;
  const start = window.scrollY;
  const startTime = performance.now();

  function animateScroll(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease = 1 - Math.pow(1 - progress, 3);
    window.scrollTo(0, start * (1 - ease));

    if (elapsed < duration) {
      requestAnimationFrame(animateScroll);
    }
  }

  requestAnimationFrame(animateScroll);
  buttonKey.value++;
}


const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300;
};
const bottomOffset = computed(() => breakpoints.smAndDown ? 105 : 100 ) ;

const changeButtonPosition = () => {
  const scrollPosition = window.scrollY + window.innerHeight;
  const docHeight = document.documentElement.scrollHeight;

  const distanceFromBottom = docHeight - scrollPosition;
  const clamped = Math.min(Math.max(distanceFromBottom, 0), bottomOffset.value);
  const remOffset = 3 + ((bottomOffset.value - clamped) / 100) * 5;

  buttonPosition.value = `${remOffset}rem`;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('scroll', changeButtonPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('scroll', changeButtonPosition);
});

</script>

<style scoped>
.back-to-top-container {
  position: fixed;
  right: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.button-top:deep(button:hover) {
  background-color: var(--secondary) !important;
  transform: scale(0.99);
  box-shadow: none;
}
</style>
